/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useCallback, useState } from 'react'

import type { AppGrant } from './types'

export interface DisabledSet {
  grants: AppGrant[]
  insights: string[]
}

/**
 * Save-with-confirmation: when nothing in use is being switched off the save runs
 * at once; otherwise it is held until the admin confirms in the modal.
 */
export const useSaveConfirm = () => {
  const [pending, setPending] = useState<{ disabled: DisabledSet; save: () => void } | null>(null)

  const request = useCallback((disabled: DisabledSet, save: () => void) => {
    if (disabled.grants.length === 0 && disabled.insights.length === 0) {
      save()
      return
    }
    setPending({ disabled, save })
  }, [])

  const confirm = useCallback(() => {
    if (!pending) return
    const { save } = pending
    setPending(null)
    save()
  }, [pending])

  const cancel = useCallback(() => setPending(null), [])

  return { pending: pending?.disabled ?? null, request, confirm, cancel }
}

export default useSaveConfirm
